import { PackageVersion } from "../models/Hardware.PackageVersion.gen";

/**
 * What a device can be asked to install or upgrade, worked out from its last package report.
 *
 * This is the one upgrade path. A device reports what apt has installed and what apt could move
 * each package to; the server turns a list of package names into a PackageUpdateCommand and the
 * device runs `apt install -y <names>` on its next check-in. The older "download this firmware
 * build" command is no longer honoured by the firmware, so every surface that offers an update —
 * the dashboard's Upgrade tab, its batch action and the Fleet Commander console — should go
 * through the helpers here rather than grow its own idea of which packages count.
 */

/**
 * Core packages offered for install even when a device has not reported them. A device only
 * reports what is already installed (`apt list --installed`), so a package being rolled out for
 * the first time (e.g. the MCP7940 RTC overlay) would otherwise never appear anywhere and could
 * not be pushed from the web. Names must match the apt repo exactly; `apt install -y <name>`
 * performs a fresh install and an upgrade the same way.
 */
export const INSTALLABLE_CORE_PACKAGES = ["os-mcp7940"];

/**
 * The apt package carrying the firmware build. Every device has it, so it is what the Upgrade tab
 * offers for a device that has never sent a package report — the hardware-specific packages
 * (sensor drivers, ISP tuning) can't be guessed safely without one.
 */
export const FIRMWARE_PACKAGE_NAME = "os-megafirmware";

/** Version shown for a package that has never been installed — apt will fetch whatever is current. */
export const FRESH_INSTALL_VERSION = "latest";

/** Omnisight's own packages, as distinct from the OS packages apt also reports. */
export function isCorePackage(name: string | undefined): boolean {
    return !!name?.startsWith("os-");
}

export type CorePackageRow = {
    /** The apt package name — what gets sent back to the server. */
    name: string;
    /** Undefined when the package is not installed at all. */
    currentVersion: string | undefined;
    /** Undefined when the package is already current; FRESH_INSTALL_VERSION for a first install. */
    upgradableTo: string | undefined;
    installed: boolean;
};

/**
 * Every core package the device has, plus the known core packages it lacks, sorted by name.
 * A row with an `upgradableTo` is one the device would actually change; the rest are listed so
 * a reader can see the unit's full complement and that it is current.
 */
export function corePackageRows(packages: PackageVersion[] | undefined): CorePackageRow[] {
    const reported = (packages ?? []).filter((pkg) => isCorePackage(pkg.name));
    const rows: CorePackageRow[] = reported.map((pkg) => ({
        name: pkg.name!,
        currentVersion: pkg.currentVersion,
        upgradableTo: pkg.upgradableTo || undefined,
        installed: true,
    }));
    for (const name of INSTALLABLE_CORE_PACKAGES) {
        if (reported.some((pkg) => pkg.name === name)) continue;
        rows.push({ name, currentVersion: undefined, upgradableTo: FRESH_INSTALL_VERSION, installed: false });
    }
    return rows.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
}

/** The names of the rows that would change — the sensible default selection for "update this unit". */
export function pendingUpgradeNames(rows: CorePackageRow[]): string[] {
    return rows.filter((row) => row.installed && row.upgradableTo !== undefined).map((row) => row.name);
}
